import * as S from "@blisspointmedia/bpm-types/dist/StreamingPerformance";
import {
  awaitJSON,
  CrossChannelLambdaFetch,
  pollS3,
  StreamingPerformanceLambdaFetch,
} from "../utils/fetch-utils";
import { SetError } from "../redux/modals";
import { KpiMappings } from "@blisspointmedia/bpm-types/dist/KpiMapping";

export const HIDE_MATCHED_RESPONSES_ALERT_KEY = "matchedResponses_hideAlert";

// The worker lambda is capped at 15 minutes, so polling longer than that can never succeed.
export const MATCHED_RESPONSES_POLL_TIMEOUT = 16 * 60;

const TVAD_SOURCE = "tvad";

// The KPI picker shows the cross-channel KPI name (e.g. "Cart Adds"), but the matched responses
// worker builds its S3 path from the TVAD KPI name (e.g. "blacktux_creatorcartadds_filtered").
// Translate via the company's KPI mappings, leaving the value untouched when there is no mapping
// (it may already be a TVAD KPI name).
export const getTvadKpi = async (company: string, kpi: string): Promise<string> => {
  const res = await CrossChannelLambdaFetch("/getKpiMappings", { params: { company } });
  const { kpiMappings } = await awaitJSON<{ kpiMappings?: KpiMappings }>(res);
  return kpiMappings?.[kpi]?.mappings[TVAD_SOURCE]?.[0]?.kpi ?? kpi;
};

export interface DownloadMatchedResponsesArgs {
  company: string;
  // The KPI as shown in the UI; translated to its TVAD KPI name before calling the lambda.
  kpi: string;
  start?: string;
  end?: string;
  impsBetween: boolean;
  prefix: S.Prefix;
  isGraph: boolean;
  agency?: string;
  overloadFilenameKpi?: string;
  setStatus?: (status: "" | "Requesting" | "Generating") => void;
  setError: SetError;
}

export const downloadMatchedResponses = async (
  args: DownloadMatchedResponsesArgs
): Promise<void> => {
  const {
    company,
    kpi,
    start = "",
    end = "",
    impsBetween,
    prefix,
    isGraph,
    agency = "bpm",
    overloadFilenameKpi,
    setStatus,
    setError,
  } = args;

  try {
    setStatus?.("Requesting");
    const tvadKpi = await getTvadKpi(company, kpi);
    let res = await StreamingPerformanceLambdaFetch<S.GetMatchedResponsesParams>(
      "/matched_responses",
      {
        params: {
          kpi: tvadKpi,
          start,
          end,
          impsBetween,
          prefix,
          agency,
          isGraph,
        },
      }
    );
    let { id, bucket, ext } = await awaitJSON<S.GetMatchedResponsesResponse>(res);
    setStatus?.("Generating");
    await pollS3({
      bucket,
      mimeType: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ext,
      filename: id,
      autoDownload: true,
      overloadFilename: `matched_responses_${overloadFilenameKpi ?? kpi}.xlsx`,
      withErrors: true,
      timeout: MATCHED_RESPONSES_POLL_TIMEOUT,
    });
  } catch (e) {
    setError({
      reportError: e,
      message: e instanceof Error ? e.message : String(e),
    });
  }
  setStatus?.("");
};
